import React, { useEffect, useState } from "react"
import { Link } from "react-router-dom"
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { useParams } from "react-router-dom"
import { Dropdown } from 'react-bootstrap'
import DatePicker from "react-datepicker"

import FilterSearch from 'pages/parts/FilterSearch'
import FilterPagination from 'pages/parts/FilterPagination'

import { httpOk, GET, DELETE } from 'helpers/api'
import { formatDate, formatMoney, returnSign } from 'helpers/helpers'

/**
 * TA'MINOTCHI KARTASI (sverka / solishtirish dalolatnomasi).
 *
 * Ilgari bu ekranda atigi 4 ustun bor edi (hujjat ID, izoh, sana, summa) va
 * ta'minotchining qog'ozi bilan solishtirib bo'lmasdi:
 *   - davr boshidagi qoldiq umuman hisoblanmasdi (filtr qatorlarni kesardi xolos);
 *   - qoldiq ustuni izohga olingan edi;
 *   - kirim / to'lov / tuzatish bir-biridan ajratilmagan edi (faqat rang);
 *   - valyuta belgisi yo'q edi, holbuki qarz $ da yuritiladi.
 *
 * Endi: boshi -> harakatlar (yuguruvchi qoldiq bilan) -> oxiri. Chop etsa bo'ladi.
 */
function Index() {
	const { t } = useTranslation()
	const { supplier_id } = useParams()

	const reduxAccount = useSelector(state => state.account)

	const [dropdownIsOpen, setDropdownIsOpen] = useState(false)
	const [filterDataExist, setFilterDataExist] = useState(false)
	/* ⚠️ size = 1000: sverka BUTUN davrni bitta varaqda talab qiladi. Backend standarti
	   20 ta edi — 95 ta harakati bor ta'minotchida 1-sahifada eng eskisi chiqib,
	   «davr boshi/oxiri» qatorlari umuman ko'rinmasdi va chop etilgan dalolatnoma
	   chala bo'lardi. */
	const [filterData, setFilterData] = useState({
		pos_id: reduxAccount.pos_id,
		search: '',
		start_date: null,
		end_date: null,
		size: 1000,
	})
	const [pagination, setPagination] = useState({
		url: `/report-suppliers-history/${supplier_id}`,
		response: null
	})
	const [data, setData] = useState({})

	/* `override` — «Tozalash» tugmasi uchun: setFilterData() darhol ta'sir qilmaydi
	   (React holati keyingi renderda yangilanadi), shuning uchun tozalangan qiymatlar
	   to'g'ridan-to'g'ri uzatiladi. Aks holda sana filtri tozalanmay qolardi. */
	async function searchWithFilter(override = null) {
		const f = override && typeof override === 'object' ? override : filterData
		var filterUrl = `/report-suppliers-history/${supplier_id}`
		var urlParams = ""

		if (f.pos_id)
			urlParams += returnSign(urlParams) + 'pos_id=' + f.pos_id
		if (f.start_date)
			urlParams += returnSign(urlParams) + 'start_date=' + f.start_date
		if (f.end_date)
			urlParams += returnSign(urlParams) + 'end_date=' + f.end_date
		if (f.search)
			urlParams += returnSign(urlParams) + 'search=' + f.search
		if (f.page)
			urlParams += returnSign(urlParams) + 'page=' + f.page
		if (f.size)
			urlParams += returnSign(urlParams) + 'size=' + f.size

		/* ⚠️ Ilgari bu shart `filterUrl !== filterUrl` ko'rinishida edi — ya'ni HECH QACHON
		   bajarilmasdi va tanlangan davr ekranda ham, chop etilgan varaqda ham
		   ko'rinmasdi. Endi haqiqiy filtr borligiga qarab belgilanadi. */
		setFilterDataExist(!!(f.start_date || f.end_date || f.search))

		filterUrl = filterUrl + urlParams

		const response = await GET(filterUrl, {}, { loader: true })
		if (httpOk(response)) {
			if (dropdownIsOpen) setDropdownIsOpen(false)
			setData(response.data)
			setPagination({ ...pagination, 'url': filterUrl, 'response': response.data })
		}
	}

	/* Tuzatishni bekor qilish — qarz aynan yozilganidek qaytadi (teskari yozuv emas,
	   qatorning o'zi yopiladi). */
	async function deleteAdjustment(item) {
		if (!window.confirm(`${t('supplier_adjustment_delete')}\n${formatMoney(item.amount)} $ — ${item.note ?? ''}`)) return

		const response = await DELETE(`/suppliers-adjustment/${item.entity_id}`, {}, { loader: true })
		if (httpOk(response)) searchWithFilter()
	}

	function resetFilter() {
		const cleared = {
			pos_id: reduxAccount.pos_id,
			search: '',
			start_date: null,
			end_date: null,
			size: 1000,
		}
		setFilterData(cleared)
		searchWithFilter(cleared)
	}

	async function paginate(page, size) {
		setFilterData({ ...filterData, 'page': page, 'size': size })
	}

	function toggleDropdown() {
		setDropdownIsOpen(!dropdownIsOpen)
	}

	/* Chop etishdan oldin `body.printing-sverka` — menyu/topbar qog'ozga chiqmasin va
	   «Тур» belgisi oq fonda oq matn bo'lib yo'qolmasin (style.css dagi @media print). */
	function printCard() {
		document.body.classList.add('printing-sverka')
		window.print()
		setTimeout(() => document.body.classList.remove('printing-sverka'), 1000)
	}

	function rowTypeName(type) {
		switch (type) {
			case 'income': return t('good_reception')
			case 'payment': return t('payment')
			case 'adjustment': return t('adjustment')
			default: return t('other_operation')
		}
	}

	function rowTypeClass(type) {
		switch (type) {
			case 'income': return 'bg-danger'
			case 'payment': return 'bg-success'
			case 'adjustment': return 'bg-primary'
			default: return 'bg-secondary'
		}
	}

	/* «Davr boshi / davr oxiri» qatorlari faqat HAMMA harakat bitta sahifaga
	   sig'gandagina ko'rsatiladi. Aks holda 2-sahifada «davr boshi» yozuvi
	   yonidagi qoldiq bilan mos kelmay, chalg'itardi. Yuqoridagi kartochkalarda
	   ikkalasi baribir turibdi (va chop etishda ham chiqadi). */
	const singlePage = (data?.data?.length ?? 0) >= Number(data?.total ?? 0)

	useEffect(() => {
		searchWithFilter(false)
	}, [filterData.search, filterData.page, filterData.size]) // eslint-disable-line react-hooks/exhaustive-deps

	return (
		<>
			<div className="page-header d-flex text-truncate">
				<h4 className="vertical-center h-100">
					{t('supplier_card')}
				</h4>
				<div className="vertical-center">
					<i className="uil uil-angle-double-right fz-20"></i>
				</div>
				<div className="vertical-center" title={data?.supplier?.supplier_id}>
					<h6>{data?.supplier?.name}</h6>
				</div>
			</div>

			{/* SVERKA JAMLANMASI: boshi -> harakat -> oxiri */}
			<div className="row mb-3">
				<div className="col-md-3">
					<div className="card h-100">
						<div className="card-body">
							<div className="text-muted fz-14">{t('opening_balance')}</div>
							<h5 className={data.opening_balance >= 0 ? 'text-success' : 'text-danger'}>
								{formatMoney(data.opening_balance)} $
							</h5>
						</div>
					</div>
				</div>
				<div className="col-md-3">
					<div className="card h-100">
						<div className="card-body">
							<div className="text-muted fz-14">{t('good_reception')}</div>
							<h5 className="text-danger">{formatMoney(data.total_income)} $</h5>
						</div>
					</div>
				</div>
				<div className="col-md-3">
					<div className="card h-100">
						<div className="card-body">
							<div className="text-muted fz-14">{t('payment')}</div>
							<h5 className="text-success">{formatMoney(data.total_payment)} $</h5>
							{!!data.total_adjustment &&
								<div className="fz-14">
									<span className="text-muted">{t('adjustment')}: </span>
									<span className={data.total_adjustment >= 0 ? 'text-success' : 'text-danger'}>
										{formatMoney(data.total_adjustment)} $
									</span>
								</div>
							}
							{/* Turkumga tushmagan eski qatorlar. Ko'rsatilmasa jamlanma
							    «boshi + kirim − to'lov = oxiri» arifmetikasi to'g'ri chiqmasdi. */}
							{!!data.total_other &&
								<div className="fz-14">
									<span className="text-muted">{t('other_operation')}: </span>
									<span className={data.total_other >= 0 ? 'text-success' : 'text-danger'}>
										{formatMoney(data.total_other)} $
									</span>
								</div>
							}
						</div>
					</div>
				</div>
				<div className="col-md-3">
					<div className="card h-100">
						<div className="card-body">
							<div className="text-muted fz-14">{t('closing_balance')}</div>
							<h5 className={data.closing_balance >= 0 ? 'text-success' : 'text-danger'}>
								{formatMoney(data.closing_balance)} $
							</h5>
							{/* Karta qoldiqni JURNALDAN qayta hisoblaydi. Agar BUTUN jurnal yig'indisi
							    ta'minotchining joriy qoldig'i bilan mos kelmasa — demak qoldiq jurnaldan
							    tashqarida o'zgartirilgan. Jimgina yashirmaymiz.
							    ⚠️ `closing_balance` bilan taqqoslash NOTO'G'RI bo'lardi: o'tgan oy tanlansa
							       keyingi harakatlar tufayli farq tabiiy ravishda chiqadi va soxta
							       ogohlantirish berardi. Shuning uchun davrdan MUSTAQIL `journal_balance`. */}
							{(Math.abs((data.journal_balance ?? 0) - (data.current_balance ?? 0)) > 0.01) &&
								<div className="fz-14 text-warning" title={`${formatMoney(data.current_balance)} $`}>
									<i className="uil uil-exclamation-triangle me-1"></i>
									{t('balance_mismatch')}
								</div>
							}
						</div>
					</div>
				</div>
			</div>

			<div className="card">
				<div className="card-body">

					<div className="d-flex justify-content-between flex-wrap mb-3 d-print-none">
						<div className="d-flex gap-2">
							<FilterSearch
								filterData={filterData}
								setFilterData={setFilterData}>
							</FilterSearch>
							<div>
								<Dropdown onToggle={toggleDropdown} show={dropdownIsOpen}>
									<Dropdown.Toggle variant="outline-primary" className="btn-wide h-100">
										{t('filter')}
									</Dropdown.Toggle>

									<Dropdown.Menu className="dropdown-filter">
										<div className="row">
											<div className="form-group col-md-6">
												<label>{t('period')}</label>
												<div className="calendar-input">
													<DatePicker
														className="form-control"
														dateFormat="dd.MM.yyyy"
														selected={filterData.start_date ? new Date(filterData.start_date) : ''}
														onChange={(date) => setFilterData({ ...filterData, 'start_date': formatDate(date, 'yyyy-MM-dd') })} />
													<i className="uil uil-calendar-alt"></i>
												</div>
											</div>
											<div className="form-group col-md-6">
												<label className="invisible">.</label>
												<div className="calendar-input">
													<DatePicker
														className="form-control"
														dateFormat="dd.MM.yyyy"
														selected={filterData.end_date ? new Date(filterData.end_date) : ''}
														onChange={(date) => setFilterData({ ...filterData, 'end_date': formatDate(date, 'yyyy-MM-dd') })} />
													<i className="uil uil-calendar-alt"></i>
												</div>
											</div>
										</div>

										<div className="row mt-3">
											<div className="col-md-12">
												<div className="d-flex justify-content-end gap-2">
													<button className="btn btn-secondary"
														onClick={() => setDropdownIsOpen(false)}>
														{t('cancel')}
													</button>
													<button className="btn btn-primary"
														onClick={() => searchWithFilter(false)}>
														{t('apply')}
													</button>
												</div>
											</div>
										</div>
									</Dropdown.Menu>
								</Dropdown>
							</div>
							<button className="btn btn-outline-primary btn-wide"
								onClick={printCard}>
								<i className="uil uil-print me-1"></i>
								{t('print')}
							</button>
						</div>
						<div className="d-flex gap-2">
							<div className="vertical-center">
								<h4><b>{t('current_debt')}</b>:</h4>
							</div>
							<div className="vertical-center">
								<h5 className="text-end">
									<span className={data.current_balance >= 0 ? 'text-success' : 'text-danger'}>
										{formatMoney(data.current_balance)} $
									</span>
								</h5>
							</div>
						</div>
					</div>

					{filterDataExist &&
						<div className="filter-block">
							<div className="filter">
								<strong>{t('filter')}</strong>
								<br />
								{(filterData.start_date || filterData.end_date) &&
									<span className="me-2">
										<b>{t('period')}: </b>
										{filterData.start_date &&
											<span>{formatDate(filterData.start_date)}</span>
										}
										{(filterData.start_date && filterData.end_date) &&
											<span>|</span>
										}
										{filterData.end_date &&
											<span>{formatDate(filterData.end_date)}</span>
										}
									</span>
								}
								{filterData.search &&
									<span className="me-2">
										<b>{t('search')}: </b>
										<span>{filterData.search}</span>
									</span>
								}
							</div>
							<div className="vertical-center d-print-none">
								<button className="btn btn-outline-dark text-uppercase" onClick={resetFilter}>{t('reset')}</button>
							</div>
						</div>
					}

					<div className="table-responsive">
						<table className="table table-bordered mb-0">
							<thead>
								<tr>
									<th style={{ 'minWidth': '33px' }} className="text-center">#</th>
									<th style={{ 'width': '12%' }} className="text-center">{t('date')}</th>
									<th style={{ 'width': '12%' }}>{t('type')}</th>
									<th style={{ 'width': '10%' }}>{t('document')}</th>
									<th style={{ 'width': '36%' }}>{t('note')}</th>
									<th style={{ 'width': '15%' }} className="text-end">{t('amount')}</th>
									<th style={{ 'width': '15%' }} className="text-end">{t('balance')}</th>
									<th style={{ 'minWidth': '33px' }} className="d-print-none"></th>
								</tr>
							</thead>
							<tbody>
								{singlePage &&
								<tr className="tr-bold table-secondary">
									<td></td>
									<td className="text-center text-nowrap">
										{filterData.start_date ? formatDate(filterData.start_date) : ''}
									</td>
									<td colSpan="3"><b>{t('opening_balance')}</b></td>
									<td></td>
									<td className={`text-end text-nowrap ${data.opening_balance >= 0 ? 'text-success' : 'text-danger'}`}>
										<b>{formatMoney(data.opening_balance)}</b>
									</td>
									<td className="d-print-none"></td>
								</tr>
								}

								{data?.data?.map((item, index) => (
									<tr key={index}>
										<td className="text-center">{item.row_number}</td>
										<td className="text-center text-nowrap">
											{formatDate(item.created_at, 'dd.MM.yyyy HH:mm')}
										</td>
										<td>
											<span className={`badge ${rowTypeClass(item.row_type)}`}>
												{rowTypeName(item.row_type)}
											</span>
										</td>
										<td>
											{item.document_in_id &&
												<Link to={`/documents-in/preview/${item.document_in_id}`}
													target="_blank" rel="noopener noreferrer" className="text-primary">
													{item.document_number ?? item.document_in_id}
												</Link>
											}
										</td>
										<td className="td-ellipsis" title={item.note}>{item.note}</td>
										<td className={`text-end text-nowrap ${item.transaction_type === 'plus' ? 'text-success' : 'text-danger'}`}>
											{item.transaction_type === 'plus' ? '+' : '−'}{formatMoney(item.amount)}
										</td>
										<td className={`text-end text-nowrap ${item.running_balance >= 0 ? 'text-success' : 'text-danger'}`}>
											{formatMoney(item.running_balance)}
										</td>
										<td className="d-print-none">
											{item.row_type === 'adjustment' &&
												<div className="table-dropdown">
													<Dropdown id={`dropdown-${index}`}>
														<Dropdown.Toggle variant="outline-primary">
															<i className="uil uil-ellipsis-v"></i>
														</Dropdown.Toggle>
														<Dropdown.Menu>
															<div className="table-dropdown-item text-danger"
																onClick={() => deleteAdjustment(item)}>
																<i className="uil-times"></i>
																{t('cancel')}
															</div>
														</Dropdown.Menu>
													</Dropdown>
												</div>
											}
										</td>
									</tr>
								))}

								{singlePage &&
								<tr className="tr-bold table-secondary">
									<td></td>
									<td className="text-center text-nowrap">
										{filterData.end_date ? formatDate(filterData.end_date) : ''}
									</td>
									<td colSpan="3"><b>{t('closing_balance')}</b></td>
									<td></td>
									<td className={`text-end text-nowrap ${data.closing_balance >= 0 ? 'text-success' : 'text-danger'}`}>
										<b>{formatMoney(data.closing_balance)}</b>
									</td>
									<td className="d-print-none"></td>
								</tr>
								}
							</tbody>
						</table>
					</div>

					{pagination.response &&
						<div className="d-print-none">
							<FilterPagination
								pagination={pagination}
								paginate={paginate}>
							</FilterPagination>
						</div>
					}
				</div>
			</div>
		</>
	)
}

export default Index
